import type { MantineColor } from "@mantine/core";
import type { ComponentType, ReactNode } from "react";

import { SkeletonPlaceholder } from "@expert/common-ui";
import { type BoxProps, type ElementProps, type MantineSpacing, Stack, Text, Transition } from "@mantine/core";

import type { ChatTask } from "@/sdk/agent/chat";

import type { ChatVariant } from "./variants";

import classes from "../chat-components.module.css";
import { AboutToEnd } from "../CustomerSections";
import { CustomerName } from "../CustomerSections/CustomerName";
import { useFormattedTimestamp } from "../hooks/useFormattedTimeStamp";
import { IncomingChatCopy } from "./IncomingChatCopy";
import { QueuedChatIndicator } from "./QueuedChatIndicator";

export interface ChatPreviewProps {
    chat: ChatTask;
    lastMessageDate: Date | null;
    variant: ChatVariant;
    variantColor: MantineColor;
}

const mapVariantToPreview = ({
    chat: { changeStateAt, customerName },
    formattedTimestamp,
    variant,
    variantColor,
}: ChatPreviewProps & { formattedTimestamp: string }): ReactNode => {
    const showSkeleton = (customerName.loading || customerName.value === undefined) && variant === "default";
    const isClosing =
        variant === "closedByCustomer" || variant === "customerDisconnected" || variant === "closedByExpert";
    const customerNameColor = variant === "inactive" || isClosing ? "dark.2" : undefined;
    const customerInfo = (
        <>
            <SkeletonPlaceholder
                height={showSkeleton ? "var(--mantine-font-size-xs)" : "auto"}
                width={"90%"}
                visible={showSkeleton}
            />
            <SkeletonPlaceholder
                height={showSkeleton ? "var(--mantine-font-size-xxs)" : "auto"}
                mt={showSkeleton ? 6 : 0}
                width={showSkeleton ? "70%" : "90%"}
                visible={showSkeleton}
            />

            <Transition mounted={!showSkeleton} transition="fade-right" duration={300} timingFunction="ease">
                {(transitionStyle) => (
                    <Stack style={{ ...transitionStyle }} className={classes.chat_preview_builder}>
                        <CustomerName customerName={customerName} size="xs" truncate={true} color={customerNameColor} />
                        {variant !== "neglected" && variant !== "inactive" && !isClosing && (
                            <Text span fw="400" size="xxs" c="dimmed">
                                {formattedTimestamp}
                            </Text>
                        )}
                    </Stack>
                )}
            </Transition>
        </>
    );

    switch (variant) {
        case "incoming":
            return <IncomingChatCopy />;
        case "neglected":
            return (
                <>
                    {customerInfo}
                    <AboutToEnd endReason="Neglected" endAt={changeStateAt} endReasonColor={variantColor} />
                </>
            );
        case "inactive":
            return (
                <>
                    {customerInfo}
                    <AboutToEnd endReason="Inactive" endAt={changeStateAt} endReasonColor={variantColor} />
                </>
            );
        case "closedByCustomer":
        case "closedByExpert":
        case "customerDisconnected":
            return (
                <>
                    {customerInfo}
                    <AboutToEnd endReason="Chat ended" endAt={changeStateAt} endReasonColor={variantColor} />
                </>
            );
        case "default":
        default:
            return <>{customerInfo}</>;
    }
};

interface ChatPreviewBuilderProps extends ChatPreviewProps {
    wrapper: ComponentType<BoxProps & ElementProps<"div"> & { gap?: MantineSpacing }>;
    unreadMessageCount: number;
}

export function ChatPreviewBuilder({
    chat,
    lastMessageDate,
    variant,
    variantColor,
    wrapper: Wrapper,
    unreadMessageCount,
}: ChatPreviewBuilderProps) {
    const formattedTimestamp = useFormattedTimestamp(lastMessageDate);
    const chatPreview = mapVariantToPreview({
        chat,
        lastMessageDate,
        formattedTimestamp,
        variant,
        variantColor,
    });

    return (
        <>
            <Wrapper className={classes.chat_preview_builder}>{chatPreview}</Wrapper>
            <QueuedChatIndicator
                color="asurion.5"
                size={16}
                label={unreadMessageCount}
                visible={variant !== "incoming" && !!unreadMessageCount}
            />
        </>
    );
}
